import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import DynamicItemTable from "../../../../../../Components/Table/DynamicItemTable";
import useModal from "../../../../../../Components/CustomModal/useModal";
import AutoReceiveModel from "./AutoReceiveModel";

const PurReceiveTable = ({
    itemsArr,
    itemErrors,
    handleItemChange,
    handleItemBlur,
    handleAddItem,
    handleRemoveItem,
    canAddMoreItems,
    isLoading,
    error,
    data,
    form,
    setItemsArr
}) => {

    const [activeModal, setActiveModal] = useState(null);
    const modal = useModal();

    const itemData = useSelector((state) => state.getItem?.user?.data) || [];

    const { totalQty, totalAmount, totalTaxable, totalGst, totalNetPost } = useMemo(() => {
        let qty = 0;
        let amount = 0;
        let taxable = 0;
        let gst = 0;
        let net = 0;

        itemsArr.forEach((item) => {
            qty += Number(item.quantity) || 0;
            amount += Number(item.amount) || 0;
            taxable += Number(item.taxable_amount) || 0;
            gst += Number(item.gst_amount) || 0;
            net += Number(item.total_amount) || 0;
        });

        return {
            totalQty: qty.toFixed(2),
            totalAmount: amount.toFixed(2),
            totalTaxable: taxable.toFixed(2),
            totalGst: gst.toFixed(2),
            totalNetPost: net.toFixed(2),
        };
    }, [itemsArr]);

    const columns = [
        { label: "Sr.", type: "index", width: "50px", align: "center" },
        {
            label: "Item Name",
            field: "item_id",
            type: "dropdown",
            options: itemData.map(e => ({ label: e?.name, value: e?._id })),
            required: true,
            minWidth: "250px"
        },
        { label: "Unit", field: "unit", type: "text", readOnly: true, minWidth: "90px" },
        { label: "Category", field: "category", type: "text", readOnly: true, minWidth: "250px" },
        {
            label: "Qty",
            field: "quantity",
            type: "number",
            required: true,
            minWidth: "80px",
            disabled: (item) => item?.isExisting === true
        },
        {
            label: "Rate (₹)",
            field: "rate",
            type: "number",
            required: true,
            width: "120px"
        },
        { label: "Amount (₹)", field: "amount", type: "text", readOnly: true, minWidth: "120px" },
        { label: "Disc. (%)", field: "discount", type: "number", width: "40px" },
        // { label: "SP Disc. %", field: "sp_discount", type: "number", width: "100px" },
        { label: "Taxable (₹)", field: "taxable_amount", type: "text", readOnly: true, minWidth: "120px" },
        { label: "GST (%)", field: "gst", type: "number", width: "40px" },
        { label: "Total (₹)", field: "total_amount", type: "text", readOnly: true, minWidth: "120px" },
        { label: "Remarks", field: "remarks", type: "text", minWidth: "200px" },
        { label: "Action", type: "action", width: "80px", align: "center" }
    ];

    const footer = [
        { label: "Total", align: "center" },
        { colSpan: 3 },
        { value: totalQty, align: "end" },
        { label: "" },
        { value: totalAmount, align: "end" },
        { label: "" },
        { value: totalTaxable, align: "end" },
        { label: "" },
        { value: totalNetPost, align: "end" },
        { label: "" },
        { label: "" },
    ];

    const handleAutoClick = () => {
        if (!form?.party_id || !form.project_id) {
            return;
        }
        setActiveModal('auto');
        modal.show();
    };

    const handleAutoItemsArr = (newItemsOrUpdater) => {
        if (typeof newItemsOrUpdater === 'function') {
            setItemsArr(prev => {
                const updated = newItemsOrUpdater(prev);
                return processAutoItems(updated, prev);
            });
        } else {
            setItemsArr(prev => processAutoItems(newItemsOrUpdater, prev));
        }
    };

    const processAutoItems = (newItems, prevItems) => {
        const isAddingNewItems = newItems.length > prevItems.length;

        if (isAddingNewItems) {
            const hasOnlyEmptyRows = prevItems.every(item =>
                !item.item_id &&
                (!item.quantity || parseFloat(item.quantity) === 0) &&
                (!item.rate || parseFloat(item.rate) === 0) &&
                !item.isExisting
            );
            const addedItems = newItems.slice(prevItems.length);
            const enrichedAddedItems = addedItems.map(item => {
                const itemDetails = itemData.find(i => i._id === item.item_id);
                return {
                    ...item,
                    unit: itemDetails?.unit?.name || item.unit || '',
                    category: itemDetails?.category?.name || item.category || '',
                    item_name: itemDetails?.name || item.item_name || ''
                };
            });
            if (hasOnlyEmptyRows) {
                return enrichedAddedItems;
            }
            const nonEmptyItems = prevItems.filter(item =>
                item.item_id ||
                (item.quantity && parseFloat(item.quantity) > 0) ||
                (item.rate && parseFloat(item.rate) > 0) ||
                item.isExisting
            );
            return [...nonEmptyItems, ...enrichedAddedItems];
        }
        return newItems;
    };

    return (
        <>
            <DynamicItemTable
                columns={columns}
                data={itemsArr}
                errors={itemErrors}
                onAdd={handleAddItem}
                onRemove={handleRemoveItem}
                onChange={handleItemChange}
                onBlur={handleItemBlur}
                footer={footer}
                canAddMore={canAddMoreItems}
                isLoading={isLoading}
                generalError={error.items}
                title="Item Details"
                addButtonLabel="Add Item"
                completeDetailsMessage="Complete the current item details (Name, Quantity, Rate) to add more items"
                showAutoButton={true}
                onAutoClick={handleAutoClick}
                autoButtonDisabled={!form?.party_id || !form.project_id}
                autoButtonTitle={(!form?.party_id || !form.project_id) ? "Please select the party and project for adding the items." : "Add Auto"}
            />

            {activeModal === 'auto' && (
                <AutoReceiveModel
                    modal={{
                        ...modal,
                        onClose: () => {
                            modal.hide();
                            setActiveModal(null);
                        }
                    }}
                    itemsArr={itemsArr}
                    setItemsArr={handleAutoItemsArr}
                    form={form}
                    data={data}
                />
            )}
        </>
    );
};

export default PurReceiveTable;

